import {
  Asset,
  AssetFilterInputObject,
  AssetsQueryVariables,
  FilterGroupingEnum,
  useAssetIdsLazyQuery,
  useAssetsQuery,
} from 'graphql-common';
import _get from 'lodash/get';
import { GetUrlParams } from '@lib/enums/urls';
import createSearchFilters from '@lib/utils/createSearchFilters';
import { ListQueryHookProps, ListQueryResultWithIds } from '../types';

export default function useAssetsQueryHook({
  additionalFilters,
  ids,
  orderingParams,
  paginationParams,
  searchQuery,
  searchQueryFilterSchema = ['name', 'code'],
  skip,
  usePrevious = true,
  withCountersOnly,
  includeFullData = false,
  includeCategoryData = false,
  includeSiteIds = false,
}: ListQueryHookProps & {
  withCountersOnly?: boolean;
  includeFullData?: boolean;
  includeCategoryData?: boolean;
  includeSiteIds?: boolean;
}): ListQueryResultWithIds<Asset> {
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(orderingParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    orderingParams,
    GetUrlParams.OrderingDirection,
  );
  const assetsQueryVariables: AssetsQueryVariables = {
    page: Number(page),
    limit: Number(perPage),
    includeFullData,
    includeCategoryData,
  };
  const filters: AssetFilterInputObject[] = [];
  if (searchQuery) {
    const searchFields = searchQueryFilterSchema.map((fieldName) => ({
      fieldName,
    }));
    filters.push(...createSearchFilters(searchQuery, searchFields));
  }
  if (withCountersOnly) {
    filters.push({
      countersCount: {
        grouping: FilterGroupingEnum.And,
        predicate: { gt: '0' },
      },
    });
  }
  if (ids?.length) {
    filters.push({
      id: {
        grouping: FilterGroupingEnum.Or,
        predicate: {
          in: ids,
        },
        filterGroup: 'ids',
      },
    });
  }
  if (additionalFilters?.length) {
    filters.push(...additionalFilters);
  }
  if (filters.length) {
    assetsQueryVariables.filters = filters;
  }
  if (orderingField && orderingDirection) {
    assetsQueryVariables.sorts = {
      [orderingField]: orderingDirection,
    };
  }
  const assetsQuery = useAssetsQuery({
    fetchPolicy: 'cache-and-network',
    variables: assetsQueryVariables,
    skip,
  });
  const [fetchIds] = useAssetIdsLazyQuery({
    fetchPolicy: 'no-cache',
    variables: {
      onlyIds: true,
      filters: assetsQueryVariables.filters,
      includeSiteId: includeSiteIds,
    },
  });
  const { loading, data, previousData, refetch, error } = assetsQuery;
  const queryData = usePrevious ? data || previousData : data;
  const firstLoading = loading && previousData === undefined;
  const collection =
    !usePrevious && loading
      ? []
      : (_get(queryData, 'data.collection', []) as Asset[]);
  const metadata = _get(queryData, 'data.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 1);

  return {
    fetchIds,
    refetch,
    collection,
    error,
    firstLoading,
    loading,
    totalPages,
    totalCount,
  };
}
